import { motion } from "framer-motion";
import { GraduationCap, ArrowRight } from "lucide-react";
import { useEnquiryPopup } from "@/context/EnquiryPopupContext";

export default function HeroBanner() {
  const scrollTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
  const { openPopup } = useEnquiryPopup();
  return (
    <section
      data-testid="hero-banner"
      className="relative pb-8 md:pb-12"
    >
      <div className="container-x">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="relative rounded-[32px] overflow-hidden border border-black/5 premium-shadow"
        >
          <img
            src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=1800&q=80"
            alt="Diverse students engaged in a modern classroom at IIUTC Study"
            className="w-full h-[300px] sm:h-[380px] md:h-[460px] lg:h-[520px] object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A3161]/85 via-[#0A3161]/60 to-transparent" />
          <div className="absolute inset-0 grid-pattern opacity-20" />

          <div className="absolute inset-0 flex items-center">
            <div className="px-6 md:px-12 lg:px-16 max-w-2xl text-white">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 backdrop-blur-md px-3.5 py-1.5 text-[11px] md:text-xs font-semibold tracking-wide"
              >
                <GraduationCap className="h-3.5 w-3.5" strokeWidth={2.25} />
                CAMPUS · LIVE COHORTS · HYBRID LEARNING
              </motion.div>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="mt-4 font-display font-extrabold text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]"
              >
                Learn on campus.
                <br />
                <span className="text-[#940835]">Grow globally.</span>
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4, duration: 0.6 }}
                className="mt-4 md:mt-5 text-sm md:text-base text-white/85 leading-relaxed max-w-xl"
              >
                State-of-the-art labs, mentor-led studios, dedicated career pods and
                a curriculum refreshed every quarter — designed to make every learner
                job-ready and world-ready.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5, duration: 0.6 }}
                className="mt-6 md:mt-7 flex flex-wrap items-center gap-3"
              >
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  data-testid="hero-banner-apply"
                  onClick={() => openPopup()}
                  className="inline-flex items-center gap-2 rounded-full bg-[#B31942] text-white px-5 md:px-6 py-3 md:py-3.5 text-sm font-semibold hover:bg-[#940835] shadow-[0_14px_40px_rgba(229,57,53,0.35)] transition-colors"
                >
                  Apply for a Program
                  <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  data-testid="hero-banner-tour"
                  onClick={() => scrollTo("#process")}
                  className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur border border-white/25 text-white px-5 md:px-6 py-3 md:py-3.5 text-sm font-semibold hover:bg-white/25 transition-colors"
                >
                  Explore the Journey
                </motion.button>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
